import React, { useState, useEffect, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ContentContainer,
  ModalTitle,
  FormContainer,
  InputLabel,
  StyledInput,
  StyledTextarea,
  InputWrapper,
  FieldsRow,
  FieldHalf,
  ButtonsContainer,
  CancelButton,
  SubmitButton,
} from "./AddCustomProcedureModal.styled";
import { Select } from "../../Select";
import { SelectOption } from "../../Select/Select";
import { cdtCodesService, CDTCode } from "../../../services/cdtCodesService";

export interface CustomProcedureFormData {
  name: string;
  cdtCodeIds: number[];
  fee: number;
  durationMinutes: number;
  description: string;
}

interface AddCustomProcedureModalContentProps {
  onAdd: (data: CustomProcedureFormData) => void | Promise<void>;
  onClose: () => void;
}

const AddCustomProcedureModalContent: React.FC<
  AddCustomProcedureModalContentProps
> = ({ onAdd, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CustomProcedureFormData>({
    name: "",
    cdtCodeIds: [],
    fee: 0,
    durationMinutes: 0,
    description: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cdtCodes, setCdtCodes] = useState<CDTCode[]>([]);
  const [cdtLoading, setCdtLoading] = useState(true);

  // Fetch CDT codes on mount
  useEffect(() => {
    const fetchCdtCodes = async () => {
      try {
        setCdtLoading(true);
        const codes = await cdtCodesService.getCDTCodes({ activeOnly: true });
        setCdtCodes(codes);
      } catch (error) {
        console.error("Error fetching CDT codes:", error);
      } finally {
        setCdtLoading(false);
      }
    };
    fetchCdtCodes();
  }, []);

  // CDT options for select
  const cdtOptions: SelectOption[] = useMemo(
    () =>
      cdtCodes.map((code) => ({
        value: code.code,
        label: `${code.code} - ${code.description}`,
        chipLabel: code.code,
      })),
    [cdtCodes],
  );

  // Get selected CDT code strings from IDs
  const selectedCdtCodeStrings = useMemo(() => {
    return formData.cdtCodeIds
      .map((id) => {
        const code = cdtCodes.find((c) => c.id === id);
        return code?.code || "";
      })
      .filter(Boolean);
  }, [formData.cdtCodeIds, cdtCodes]);

  const handleInputChange = (
    field: keyof CustomProcedureFormData,
    value: string | number | number[],
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle CDT code selection change
  const handleCdtCodeChange = (
    value: string | number | (string | number)[],
  ) => {
    const codeStrings = Array.isArray(value)
      ? (value as string[])
      : [value as string];

    const codeIds = codeStrings
      .map((codeStr) => {
        const cdtCode = cdtCodes.find((c) => c.code === codeStr);
        return cdtCode?.id || 0;
      })
      .filter((id) => id > 0);

    handleInputChange("cdtCodeIds", codeIds);

    // Auto-calculate fee and duration from selected codes
    const selectedCodesData = codeIds
      .map((id) => cdtCodes.find((c) => c.id === id))
      .filter(Boolean) as CDTCode[];

    if (selectedCodesData.length > 0) {
      const totalFee = selectedCodesData.reduce(
        (sum, code) => sum + (code.defaultFee || 0),
        0,
      );
      const totalDuration = selectedCodesData.reduce(
        (sum, code) => sum + (code.defaultDurationMinutes || 0),
        0,
      );

      setFormData((prev) => ({
        ...prev,
        cdtCodeIds: codeIds,
        fee: totalFee,
        durationMinutes: totalDuration,
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || formData.cdtCodeIds.length === 0) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onAdd(formData);
      onClose();
    } catch (error) {
      console.error("Error adding custom procedure:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name.trim() && formData.cdtCodeIds.length > 0;

  return (
    <ContentContainer>
      <ModalTitle>
        {t("addCustomProcedureModal.title", "Add Custom Procedure")}
      </ModalTitle>

      <FormContainer>
        <InputWrapper>
          <InputLabel>
            {t("addCustomProcedureModal.procedureName", "Procedure Name")}
          </InputLabel>
          <StyledInput
            type="text"
            placeholder={t(
              "addCustomProcedureModal.procedureNamePlaceholder",
              "Enter procedure name",
            )}
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            disabled={isSubmitting}
          />
        </InputWrapper>

        <InputWrapper>
          <Select
            label={t("addCustomProcedureModal.cdtCode", "CDT Code")}
            placeholder={t(
              "addCustomProcedureModal.cdtCodePlaceholder",
              "Select CDT codes",
            )}
            value={selectedCdtCodeStrings}
            onChange={handleCdtCodeChange}
            options={cdtOptions}
            multiple
            searchable
            showChips
            loading={cdtLoading}
            fullWidth
          />
        </InputWrapper>

        <FieldsRow>
          <FieldHalf>
            <InputLabel>
              {t("addCustomProcedureModal.duration", "Duration, min")}
            </InputLabel>
            <StyledInput
              type="number"
              min="0"
              placeholder="0"
              value={formData.durationMinutes || ""}
              onChange={(e) =>
                handleInputChange(
                  "durationMinutes",
                  parseInt(e.target.value) || 0,
                )
              }
              disabled={isSubmitting}
            />
          </FieldHalf>
          <FieldHalf>
            <InputLabel>
              {t("addCustomProcedureModal.fee", "Fee, $")}
            </InputLabel>
            <StyledInput
              type="number"
              min="0"
              step="0.01"
              placeholder="0"
              value={formData.fee || ""}
              onChange={(e) =>
                handleInputChange("fee", parseFloat(e.target.value) || 0)
              }
              disabled={isSubmitting}
            />
          </FieldHalf>
        </FieldsRow>

        <InputWrapper>
          <InputLabel>
            {t("addCustomProcedureModal.description", "Description")}
          </InputLabel>
          <StyledTextarea
            placeholder={t(
              "addCustomProcedureModal.descriptionPlaceholder",
              "Add description...",
            )}
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            disabled={isSubmitting}
          />
        </InputWrapper>
      </FormContainer>

      <ButtonsContainer>
        <CancelButton
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
        >
          {t("addCustomProcedureModal.cancel", "Cancel")}
        </CancelButton>
        <SubmitButton
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !isFormValid}
        >
          {isSubmitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("addCustomProcedureModal.addProcedure", "Add Procedure")
          )}
        </SubmitButton>
      </ButtonsContainer>
    </ContentContainer>
  );
};

export default AddCustomProcedureModalContent;
